import { useState, useEffect } from "react";
import axios from "axios";
import { API, OPERATION_NAMES } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Check, X } from "@phosphor-icons/react";

function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [outsourceProcesses, setOutsourceProcesses] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState({ name: "", contact: "", processes: [] });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", contact: "", processes: [] });

  useEffect(() => {
    fetchSuppliers();
    fetchOutsourceProcesses();
  }, []);

  const fetchSuppliers = async () => {
    try { setSuppliers((await axios.get(`${API}/suppliers`)).data); }
    catch { toast.error("Failed to fetch suppliers"); }
  };

  const fetchOutsourceProcesses = async () => {
    try {
      const r = await axios.get(`${API}/outsource-processes`, { skipPeriod: true });
      setOutsourceProcesses(r.data?.items || []);
    } catch { /* keep empty list — user can still save without processes */ }
  };

  // Display helper: prefer the tenant's customized name, then OPERATION_NAMES, then prettify
  const procName = (code) => {
    const found = outsourceProcesses.find(p => p.code === code);
    if (found?.name) return found.name;
    if (OPERATION_NAMES[code]) return OPERATION_NAMES[code];
    return String(code).replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/suppliers`, form);
      toast.success("Supplier added");
      fetchSuppliers();
      setIsOpen(false);
      setForm({ name: "", contact: "", processes: [] });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add supplier"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this supplier?")) return;
    try { await axios.delete(`${API}/suppliers/${id}`); toast.success("Deleted"); fetchSuppliers(); }
    catch { toast.error("Failed to delete"); }
  };

  const handleEdit = (s) => {
    setEditingId(s.id);
    setEditForm({ name: s.name || "", contact: s.contact || "", processes: s.processes || [] });
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/suppliers/${editingId}`, editForm);
      toast.success("Supplier updated");
      setEditingId(null);
      fetchSuppliers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
  };

  const toggleProcess = (sp) => {
    setForm(prev => ({
      ...prev, processes: prev.processes.includes(sp) ? prev.processes.filter(p => p !== sp) : [...prev.processes, sp]
    }));
  };

  const toggleEditProcess = (sp) => {
    setEditForm(prev => ({
      ...prev, processes: prev.processes.includes(sp) ? prev.processes.filter(p => p !== sp) : [...prev.processes, sp]
    }));
  };

  return (
    <Layout>
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-[#0F172A]" style={{fontFamily: 'Chivo'}}>Suppliers</h1>
            <p className="text-sm text-[#64748B] mt-1">Manage outsource suppliers</p>
          </div>
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1D2951] hover:bg-[#2A3B73] text-white min-h-[44px]" data-testid="add-supplier-button">
                <Plus size={18} className="mr-2" /> Add Supplier
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="text-lg font-bold text-[#1D2951]">Add Supplier</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div><Label className="text-xs font-semibold">Supplier Name *</Label>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="min-h-[44px]" data-testid="supplier-name-input" /></div>
                <div><Label className="text-xs font-semibold">Contact</Label>
                  <Input value={form.contact} onChange={e => setForm({...form, contact: e.target.value})} className="min-h-[44px]" /></div>
                <div>
                  <Label className="text-xs font-semibold mb-2 block">Outsource Processes Handled</Label>
                  {outsourceProcesses.length === 0 ? (
                    <p className="text-xs text-[#94A3B8] italic p-3 border rounded">
                      No outsource processes configured. Go to <b>Customization → Outsource Processes</b> to add some.
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2" data-testid="add-supplier-process-grid">
                      {outsourceProcesses.map(sp => (
                        <label key={sp.code} className="flex items-center gap-2 p-2 border rounded text-xs cursor-pointer">
                          <Checkbox checked={form.processes.includes(sp.code)} onCheckedChange={() => toggleProcess(sp.code)} />
                          {sp.name}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <Button type="submit" className="w-full bg-[#1D2951] hover:bg-[#2A3B73] text-white min-h-[44px]">Add Supplier</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="border border-[#E2E8F0]">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="suppliers-table">
              <thead className="bg-[#1D2951] text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Processes</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E2E8F0]">
                {suppliers.map(s => (
                  <tr key={s.id} className="hover:bg-[#F8FAFC]" data-testid={`supplier-row-${s.name}`}>
                    <td className="px-4 py-3">
                      {editingId === s.id ? (
                        <Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} className="min-h-[36px] text-sm w-48" data-testid="edit-supplier-name" />
                      ) : (
                        <span className="font-semibold text-[#1D2951]">{s.name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingId === s.id ? (
                        <Input value={editForm.contact} onChange={e => setEditForm({...editForm, contact: e.target.value})} className="min-h-[36px] text-sm w-40" data-testid="edit-supplier-contact" />
                      ) : (
                        <span className="text-sm">{s.contact || "-"}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingId === s.id ? (
                        <div className="flex flex-wrap gap-1" data-testid={`edit-supplier-process-grid-${s.name}`}>
                          {outsourceProcesses.length === 0 ? (
                            <span className="text-[11px] text-[#94A3B8] italic">No outsource processes configured.</span>
                          ) : outsourceProcesses.map(sp => (
                            <label key={sp.code} className={`flex items-center gap-1 px-2 py-1 border rounded text-xs cursor-pointer ${editForm.processes.includes(sp.code) ? 'bg-[#1D2951] text-white border-[#1D2951]' : 'bg-white text-[#475569]'}`}>
                              <Checkbox checked={editForm.processes.includes(sp.code)} onCheckedChange={() => toggleEditProcess(sp.code)} className="h-3 w-3" />
                              {sp.name}
                            </label>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm">{(s.processes || []).map(p => procName(p)).join(", ") || "-"}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {editingId === s.id ? (
                        <div className="flex items-center justify-center gap-1">
                          <Button size="sm" onClick={handleSaveEdit} className="bg-[#059669] hover:bg-[#047857] text-white h-8 px-3 text-xs" data-testid="save-edit-supplier">
                            <Check size={14} />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="h-8 px-3 text-xs">
                            <X size={14} />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(s)} className="h-8 px-2" data-testid={`edit-supplier-${s.name}`}>
                            <PencilSimple size={14} />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDelete(s.id)} className="text-[#EF4444] hover:bg-red-50 h-8 px-2" data-testid={`delete-supplier-${s.name}`}>
                            <Trash size={14} />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {suppliers.length === 0 && <div className="text-center py-12 text-[#64748B]">No suppliers added yet.</div>}
          </div>
        </Card>
      </div>
    </Layout>
  );
}

export default Suppliers;
